import moment from 'moment';
import 'moment/locale/en-au';
import 'moment/locale/en-gb';
import 'moment/locale/en-ca';
import 'moment/locale/fr-ca';

/** moment has no en-us bundle; locale('en-us') silently uses en (Sunday-first). */
const MOMENT_LOCALE_BY_INTL: Record<string, string> = {
  'en-us': 'en',
};

/**
 * Semantic keys used to select a pre-defined Intl.DateTimeFormat option set.
 * Use a key rather than a raw format string so the output is automatically
 * locale-aware (en-AU → 03/10/2026, en-US → 10/03/2026, fr-CA → 2026-10-03).
 *
 * Key           | Example (en-AU)         | Replaces
 * ------------- | ----------------------- | ------------------------------------------
 * short         | 03/10/2026              | dd/MM/yyyy, DD/MM/YYYY
 * short-2y      | 03/10/26                | dd/MM/yy
 * medium        | 3 Oct 2026              | d MMM yyyy, D MMM YYYY
 * medium-short  | 3 Oct 26                | dd MMM yy, DD MMM YY
 * day-month     | 3 Oct                   | d MMM, DD MMM
 * day-month-numeric | 15/04 (en-AU) 04/15 (en-US) | numeric day+month, no year
 * weekday-short | Thu 3 Oct               | EEE d MMM, ddd DD MMM
 * weekday-medium| Thu 3 Oct 2026          | EEE dd/MM/yyyy, ddd DD MMM YYYY
 * weekday-long-month | Thu 3 October      | ddd, D MMMM
 * full          | Thursday 3 October 2026 | EEEE d MMMM yyyy, dddd MMM Do YYYY
 */
type DatePresetKey =
  | 'short'
  | 'short-2y'
  | 'medium'
  | 'medium-short'
  | 'day-month'
  | 'day-month-numeric'
  | 'weekday-short'
  | 'weekday-medium'
  | 'weekday-long-month'
  | 'full';

// '2-digit' on day/month ensures leading zeros (03 not 3) for numeric-only presets.
// Text-month presets use 'numeric' since "03 Oct" looks unnatural.
const DATE_PRESETS: Record<DatePresetKey, Intl.DateTimeFormatOptions> = {
  'short':               { day: '2-digit', month: '2-digit', year: 'numeric' },
  'short-2y':            { day: '2-digit', month: '2-digit', year: '2-digit' },
  'medium':              { day: 'numeric', month: 'short',   year: 'numeric' },
  'medium-short':        { day: 'numeric', month: 'short',   year: '2-digit' },
  'day-month':           { day: 'numeric', month: 'short' },
  'day-month-numeric':   { day: '2-digit', month: '2-digit' },  // e.g. 15/04 (en-AU), 04/15 (en-US)
  'weekday-short':       { weekday: 'short', day: 'numeric', month: 'short' },
  'weekday-medium':      { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' },
  'weekday-long-month':  { weekday: 'short', day: 'numeric', month: 'long' },
  'full':                { weekday: 'long',  day: 'numeric', month: 'long',  year: 'numeric' },
};

/**
 * Central locale/formatting service for the Diary app.
 *
 * - Reads window.config.supportedLanguages (set per environment in config/config.*.js)
 * - Matches the browser locale against the supported list at runtime
 * - Provides formatDate and formatCurrency helpers backed by Intl APIs
 *
 * Both Angular (via localizeDate / localizeCurrency filters) and React
 * (via IntlService delegation) route through this single source of truth.
 */
export class LocaleService {
  // Cache Intl.DateTimeFormat instances by "locale:preset" to avoid recreating them on every render
  private static cache = new Map<string, Intl.DateTimeFormat>();

  // Cache the computed field order per locale (result never changes for the same locale)
  private static fieldOrderCache = new Map<string, { day: number; month: number; year: number }>();

  static getLocale(): string {
    const supported: string[] = (window as any)?.config?.supportedLanguages || [];
    const browser = navigator.language; // e.g. 'en-US', 'fr-CA'

    // If the app has no supported languages configured, honour the browser locale directly
    if (!supported.length) return browser;

    // Exact match: browser locale is explicitly listed (e.g. browser='en-AU', supported includes 'en-AU')
    if (supported.includes(browser)) return browser;

    // Language-prefix match: browser is 'en-GB' but supported has 'en-AU' → use 'en-AU'
    const prefix = browser.split('-')[0];
    const prefixMatch = supported.find(l => l.startsWith(prefix + '-'));
    if (prefixMatch) return prefixMatch;

    // No match at all — fall back to the deployment's primary locale (first in the list)
    return supported[0];
  }

  static getMomentLocale(): string {
    const intlLocale = LocaleService.getLocale().toLowerCase();
    return MOMENT_LOCALE_BY_INTL[intlLocale] ?? intlLocale;
  }

  /** Align global moment with MUI DatePicker so week-start and weekday headers match. */
  /*
  After localization, opening the calendar could leave global moment on en-au (from locale imports) while MUI used en-us → en (Sunday-first). 
  Weekday headers and day cells disagreed → dates shifted one column (e.g. Tue 26 under Wed).
  This aligns both to the same locale, so the grid and headers use the same week-start rule.
  */
  static configureMomentLocale(): string {
    const momentLocale = LocaleService.getMomentLocale();
    moment.locale(momentLocale);
    return momentLocale;
  }

  /**
   * Returns the locale-aware CSS flex order (1/2/3) for day, month, and year input fields.
   *
   * Reuses the cached 'short' preset formatter and calls formatToParts() to detect the
   * natural field sequence for the active locale — ordering is the same regardless of
   * '2-digit' vs 'numeric', so no extra Intl.DateTimeFormat instance is needed.
   *
   * e.g. en-AU → { day: 1, month: 2, year: 3 }   (D/M/Y)
   *      en-US → { month: 1, day: 2, year: 3 }    (M/D/Y)
   *      fr-CA → { year: 1, month: 2, day: 3 }    (Y/M/D)
   */
  static getDateFieldOrder(): { day: number; month: number; year: number } {
    const locale = LocaleService.getLocale();

    if (LocaleService.fieldOrderCache.has(locale)) {
      return LocaleService.fieldOrderCache.get(locale)!;
    }

    // Ensure the 'short' formatter is in the main cache, then use formatToParts on it
    const cacheKey = `${locale}:short`;
    if (!LocaleService.cache.has(cacheKey)) {
      LocaleService.cache.set(cacheKey, new Intl.DateTimeFormat(locale, DATE_PRESETS['short']));
    }
    const sequence = LocaleService.cache.get(cacheKey)!
      .formatToParts(new Date(2021, 0, 15))
      .filter(p => p.type === 'day' || p.type === 'month' || p.type === 'year')
      .map(p => p.type as 'day' | 'month' | 'year');

    const order = {
      day:   sequence.indexOf('day')   + 1,
      month: sequence.indexOf('month') + 1,
      year:  sequence.indexOf('year')  + 1,
    };
    LocaleService.fieldOrderCache.set(locale, order);
    return order;
  }

  /**
   * Formats a date using a semantic preset key.
   * Accepts a Date object or an ISO string; returns '' for null/invalid input.
   */
  static formatDate(date: Date | string, preset: DatePresetKey): string {
    if (!date) return '';
    const jsDate = typeof date === 'string' ? new Date(date) : date;
    if (isNaN(jsDate.getTime())) return '';
    const locale = LocaleService.getLocale();
    const cacheKey = `${locale}:${preset}`;
    if (!LocaleService.cache.has(cacheKey)) {
      LocaleService.cache.set(cacheKey, new Intl.DateTimeFormat(locale, DATE_PRESETS[preset]));
    }
    return LocaleService.cache.get(cacheKey)!.format(jsDate);
  }

  /**
   * Formats a monetary amount using the active locale and the given ISO currency code.
   * e.g. formatCurrency(12.5, 'AUD') → '$12.50' (en-AU), 'US$12.50' (fr-CA)
   *
   * If no currency code is available, falls back to a plain decimal (no symbol):
   * e.g. formatCurrency(12.5, '') → '12.50'
   */
  /**
   * Formats a date for use in file names: locale-aware ordering, but all separators
   * (slashes, hyphens, dots, spaces) replaced with underscores so the result is safe
   * on all operating systems.
   *
   * e.g. formatDateForFilename(date, 'short')
   *   en-AU → 15_04_2026
   *   en-US → 04_15_2026
   *   fr-CA → 2026_04_15
   */
  static formatDateForFilename(date: Date | string, preset: DatePresetKey = 'short'): string {
    return LocaleService.formatDate(date, preset).replace(/[\/\-.\s]/g, '_');
  }

  static formatCurrency(amount: number, currencyCode: string): string {
    if (amount == null) return '';
    const locale = LocaleService.getLocale();
    if (!currencyCode) {
      return new Intl.NumberFormat(locale, {
        style: 'decimal',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(amount);
    }
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: currencyCode,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  }
}
